import { useQuery, useQueryClient } from "react-query";
import { useMemo } from "react";
import { PublicCalendarEventSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { PaginatorContract } from "@shared/services/api/types";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import { CacheParams } from "./CalendarCache";
import {
  CalendarEventService,
  CalendarCacheWrapper,
  CalendarQueryData,
} from "./CalendarEventService";

/**
 * Prefix for all calendar-related query keys.
 * Used for cache invalidation across all calendar queries.
 */
export const CALENDAR_CACHE_PREFIX = "calendar-cache";
export const CALENDAR_EVENTS_PREFIX = "calendar-events";

/**
 * React Query key generator for calendar cache instances.
 * This creates a key for the cache instance, not for individual queries.
 */
export function makeCalendarCacheKey(params: CacheParams): string[] {
  const keyParts = [
    `venue:${params.slug}`,
    `qty:${params.quantity_requested ?? ""}`,
    `pr:${params.price_range ?? ""}`,
    `events:${params.event_id__in ?? ""}`,
    `onlySpec:${params.only_special_events}`,
    `onlyPar:${params.only_parents ?? ""}`,
    `first:${params.find_first_event}`,
    `psp:${params.purchase_source_platform ?? ""}`,
  ];

  return [CALENDAR_CACHE_PREFIX, keyParts.join("|")];
}

/**
 * Normalizes a date by rounding down to the nearest minute.
 * This reduces React Query key variations caused by millisecond differences
 * from rapid user interactions or component re-renders.
 */
function normalizeDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setSeconds(0, 0); // Remove seconds and milliseconds
  return normalized;
}

/**
 * React Query key generator for calendar events queries.
 * Normalizes dates to prevent excessive cache key variations from timestamp differences.
 */
export function makeCalendarQueryKey(
  params: CacheParams,
  startDate: Date,
  endDate?: Date
): string[] {
  const cacheKey = makeCalendarCacheKey(params);

  // Normalize dates to nearest minute to reduce React Query cache key explosion
  // from millisecond-level timestamp variations in rapid user interactions
  const normalizedStart = normalizeDate(startDate).toISOString();
  const normalizedEnd = endDate
    ? normalizeDate(endDate).toISOString()
    : "no-end-date";

  const dateKey = `${normalizedStart}-${normalizedEnd}`;

  return [CALENDAR_EVENTS_PREFIX, cacheKey[1], dateKey];
}

export interface UsePublicCalendarEventsV2Options {
  startDate: Date;
  endDate?: Date;
  staleTime?: number;
  enabled?: boolean;
  onSuccess?: (data: PaginatorContract<PublicCalendarEventSerializer>) => void;
}

/**
 * Enhanced hook that uses React Query with embedded CalendarCache logic.
 * The caching strategy is implemented directly in the query function.
 *
 * This hook uses react-query `useQuery` directly due to the custom caching mechanism in play
 * that would be incompatible or hard to work with with our useModelService. This is a deliberate decision.
 */
export const usePublicCalendarEventsV2 = (
  params: CacheParams,
  options: UsePublicCalendarEventsV2Options
) => {
  const {
    startDate,
    endDate,
    staleTime = 60 * 1000 * 4,
    enabled = true,
    onSuccess,
  } = options;
  const queryClient = useQueryClient();
  const repository = useMemo(() => new PublicBasedVenuesRepository(), []);

  // Create stable service instance
  const service = useMemo(
    () => new CalendarEventService(repository, staleTime),
    [repository, staleTime]
  );

  // Generate consistent query key and cache key
  const cacheKey = useMemo(() => makeCalendarCacheKey(params), [params]);
  const queryKey = useMemo(
    () => makeCalendarQueryKey(params, startDate, endDate),
    [params, startDate, endDate]
  );

  const queryResult = useQuery<CalendarQueryData>(
    queryKey,
    async () => {
      // Get existing cache data from React Query
      const existingCacheData =
        queryClient.getQueryData<CalendarCacheWrapper>(cacheKey);

      // Delegate all business logic to the service
      const result = await service.fetchEvents(
        params,
        startDate,
        endDate,
        existingCacheData,
        onSuccess
      );

      // Update React Query cache with the new wrapper
      queryClient.setQueryData(cacheKey, result.cacheWrapper);

      // Return query data for React Query
      return result.queryData;
    },
    {
      enabled: enabled && Boolean(params.slug),
      staleTime,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  // Extract the actual event data from the query result
  const eventData = useMemo(
    () =>
      CalendarEventService.processEventData(
        queryResult.data,
        startDate,
        endDate
      ),
    [queryResult.data, startDate, endDate]
  );

  const isFromCache = !queryResult.isFetching && queryResult.isFetched;

  return {
    data: eventData,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    refetch: queryResult.refetch,
    status: queryResult.status,
    error: queryResult.error,
    isError: queryResult.isError,
    isSuccess: queryResult.isSuccess,
    cacheInfo: {
      isFromCache,
      cachedIntervals: queryResult.data?.cache?.cachedIntervals || [],
      totalCachedEvents: eventData?.results?.length || 0,
    },
  };
};
